/**
 * Archive Management Page
 *
 * Admin interface for managing audit log archives.
 * Provides status overview, archive list, and manual archive triggering.
 */

import { useState } from 'react';
import { RefreshCw, Loader2, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import {
  useArchiveStatus,
  useTriggerArchive,
  useArchivePreview,
} from '@/hooks/use-archive-management';
import {
  ArchiveStatusCard,
  ArchivedMonthsList,
  ArchiveTriggerForm,
  ArchiveConfirmDialog,
} from '@/components/archive-management';

/**
 * Archive Management Page Component
 */
export function ArchiveManagementPage() {
  const { toast } = useToast();

  // State for archive confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingArchive, setPendingArchive] = useState<{
    year: number;
    month: number;
    dryRun: boolean;
  } | null>(null);

  // Queries
  const {
    data: status,
    isLoading: isStatusLoading,
    isFetching: isStatusFetching,
    error: statusError,
    refetch: refetchStatus,
  } = useArchiveStatus();

  // Get preview for pending archive
  const { data: preview } = useArchivePreview(
    pendingArchive?.year ?? null,
    pendingArchive?.month ?? null
  );

  // Mutations
  const triggerArchiveMutation = useTriggerArchive();

  // Handlers
  const handleTriggerClick = (year: number, month: number, dryRun: boolean) => {
    setPendingArchive({ year, month, dryRun });
    setConfirmDialogOpen(true);
  };

  const handleArchiveConfirm = () => {
    if (!pendingArchive) return;

    triggerArchiveMutation.mutate(
      {
        year: pendingArchive.year,
        month: pendingArchive.month,
        dryRun: pendingArchive.dryRun,
      },
      {
        onSuccess: data => {
          const monthName = new Date(
            pendingArchive.year,
            pendingArchive.month - 1
          ).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

          if (pendingArchive.dryRun) {
            toast({
              title: 'Preview Complete',
              description: `Preview for ${monthName}: ${data.result.recordCount.toLocaleString()} records would be archived.`,
            });
          } else {
            toast({
              title: 'Archive Complete',
              description: `${monthName}: ${data.result.recordCount.toLocaleString()} records archived, ${data.result.deletedCount.toLocaleString()} deleted from database.`,
            });
          }
          setConfirmDialogOpen(false);
          setPendingArchive(null);
        },
        onError: error => {
          toast({
            title: 'Archive Failed',
            description: error.message || 'Failed to trigger archive.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  // Loading state
  if (isStatusLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading archive status...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (statusError) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Alert variant="destructive">
          <AlertTitle>Error Loading Archive Data</AlertTitle>
          <AlertDescription>
            {statusError instanceof Error
              ? statusError.message
              : 'Failed to load archive status. Please try again.'}
          </AlertDescription>
        </Alert>
        <Button onClick={() => refetchStatus()} className="mt-4">
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Archive className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Archive Management</h1>
            <p className="text-sm text-muted-foreground">
              Manage audit log archives to S3 Glacier storage
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetchStatus()}
          disabled={isStatusFetching}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isStatusFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Archive Status Card */}
      {status && (
        <ArchiveStatusCard
          config={status.config}
          nextScheduledArchive={status.nextScheduledArchive}
        />
      )}

      {/* Archive Trigger Form */}
      {status && (
        <ArchiveTriggerForm
          onTrigger={handleTriggerClick}
          isTriggering={triggerArchiveMutation.isPending}
          retentionDays={status.config.retentionDays}
        />
      )}

      {/* Archived Months List */}
      {status && <ArchivedMonthsList archives={status.archives} isLoading={isStatusLoading} />}

      {/* Archive Confirmation Dialog */}
      {pendingArchive && (
        <ArchiveConfirmDialog
          open={confirmDialogOpen}
          onOpenChange={open => {
            setConfirmDialogOpen(open);
            if (!open) setPendingArchive(null);
          }}
          year={pendingArchive.year}
          month={pendingArchive.month}
          estimatedCount={preview?.estimatedRecordCount ?? 0}
          dryRun={pendingArchive.dryRun}
          onConfirm={handleArchiveConfirm}
          isArchiving={triggerArchiveMutation.isPending}
        />
      )}
    </div>
  );
}

/**
 * Export as named component for router
 */
export const Component = ArchiveManagementPage;
